import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, formatDate, formatLKR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import DividendTimeline from "@/components/dividends/DividendTimeline";
import { CompanyLink } from "@/components/common/StockLinks";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown, Search, RefreshCw, ShieldCheck, Info } from "lucide-react";
import { BarChart, Bar, Cell, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const DividendsPage = () => {
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState("");
  const [allDividends, setAllDividends] = useState([]);
  const [companyDividends, setCompanyDividends] = useState([]);
  const [companyFund, setCompanyFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    Promise.all([apiGet("/companies"), apiGet("/dividends?limit=80")])
      .then(([cs, all]) => {
        // Sort dropdown alphabetically by company_code (A→Z) so users can scan quickly.
        const sorted = [...cs].sort((a, b) =>
          (a.company_code || "").localeCompare(b.company_code || "")
        );
        setCompanies(sorted);
        setAllDividends(all);
        if (sorted.length) {
          // Default selection — pick first company that actually has dividend records
          const codesWithDivs = new Set((all || []).map((d) => d.company_code));
          const firstWith = sorted.find((c) => codesWithDivs.has(c.company_code));
          setSelected((firstWith || sorted[0]).company_code);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected) return;
    apiGet(`/dividends/${selected}`).then(setCompanyDividends).catch(() => setCompanyDividends([]));
    // Pull the latest TV fundamentals so we can show live yield + DPS even when
    // we have no declared-dividend history on file yet.
    apiGet(`/companies/${selected}/full`)
      .then((bundle) => {
        const voting = (bundle.securities || []).find((s) => s.security?.share_type === "voting") || (bundle.securities || [])[0];
        setCompanyFund(voting?.latest_fundamentals || null);
      })
      .catch(() => setCompanyFund(null));
  }, [selected]);

  const yearChart = useMemo(() => {
    const map = {};
    for (const d of companyDividends) {
      // Phase 5 — bug fix: previous filter used `d.type` (capital E "Estimated")
      // but API returns `dividend_type: "estimated"`. Use the canonical field
      // and the `is_estimated` flag so estimated rows stay out of the chart.
      if (d.is_estimated || (d.dividend_type || "").toLowerCase() === "estimated") continue;
      // Phase 5 (Item 19) — Yield/total must use CASH dividends only.
      // Scrip = bonus shares, no cash payment to holder.
      if ((d.dividend_subtype || "cash") === "scrip") continue;
      if (!d.ex_dividend_date) continue;
      const y = new Date(d.ex_dividend_date).getFullYear();
      map[y] = (map[y] || 0) + (d.dividend_per_share || 0);
    }
    const rows = Object.entries(map)
      .map(([year, total]) => ({ year: String(year), total: Number(total.toFixed(2)), estimated: false }))
      .sort((a, b) => Number(a.year) - Number(b.year));
    // Issue #3 — if we have no declared history but TV has an estimated
    // DPS, show a single "est." bar for the current year so the chart
    // isn't blank.
    if (rows.length === 0 && companyFund?.estimated_dps) {
      rows.push({
        year: String(new Date().getFullYear()),
        total: Number(Number(companyFund.estimated_dps).toFixed(2)),
        estimated: true,
      });
    }
    return rows;
  }, [companyDividends, companyFund]);

  // Issue #3 — friendly "not enough data" note under the chart when only
  // one data point is available.
  const yearChartNote = useMemo(() => {
    if (yearChart.length === 0) return null;
    if (yearChart.some((r) => r.estimated)) {
      return "Only a TradingView estimated DPS is available — bars will fill in as more annual reports are analysed.";
    }
    if (yearChart.length === 1) {
      return "Only 1 year of dividend data available. Chart will fill in as more annual reports are analysed.";
    }
    return null;
  }, [yearChart]);

  // Phase 5 — Lifetime DPS must exclude scrip + estimated rows so the
  // headline figure reflects actual cash returned to shareholders.
  const totalDPS = companyDividends.reduce((s, d) => {
    const isEst = d.is_estimated || (d.dividend_type || "").toLowerCase() === "estimated";
    const isScrip = (d.dividend_subtype || "cash") === "scrip";
    if (isEst || isScrip) return s;
    return s + (d.dividend_per_share || 0);
  }, 0);
  const lastEx = companyDividends.find(
    (d) => !(d.is_estimated || (d.dividend_type || "").toLowerCase() === "estimated")
  )?.ex_dividend_date;

  // Distinct companies with at least one dividend record (across the recent feed).
  const distinctDividendCompanies = useMemo(() => {
    const set = new Set((allDividends || []).map((d) => d.company_code));
    return set.size;
  }, [allDividends]);

  const selectedCompany = companies.find((c) => c.company_code === selected);

  // Admin: load last sync timestamp + counts
  useEffect(() => {
    if (!isAdmin) return;
    apiGet("/admin/sync-dividend-announcements/last-run")
      .then((r) => setLastSync(r.last_run || null))
      .catch(() => {});
  }, [isAdmin]);

  const handleSyncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await apiPost("/admin/sync-dividend-announcements?sync=true", {});
      toast.success(
        `Sync complete — ${res.inserted ?? 0} inserted · ${res.updated ?? 0} updated`,
        {
          description: `Fetched ${res.fetched ?? 0} announcements, ${res.matched_dividends ?? 0} dividends. Estimated rows are now flagged Declared.`,
        }
      );
      // Reload the dividends feed and sync status
      const [all, lr] = await Promise.all([
        apiGet("/dividends?limit=80"),
        apiGet("/admin/sync-dividend-announcements/last-run"),
      ]);
      setAllDividends(all);
      setLastSync(lr.last_run || null);
      if (selected) {
        const cs = await apiGet(`/dividends/${selected}`);
        setCompanyDividends(cs);
      }
    } catch (e) {
      toast.error("Dividend sync failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState description={error} />;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6" data-testid="dividends-page">
      {/* Test 4 Fix 7 — public-facing note explaining how dividend dates
          are sourced and updated. Visible to ALL users (not just admins). */}
      <Card
        className="border-dashed border-primary/30 bg-primary/5"
        data-testid="dividends-info-banner"
      >
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-start gap-2.5 text-xs leading-relaxed">
            <Info className="h-4 w-4 shrink-0 text-primary mt-0.5" />
            <p className="text-muted-foreground">
              <span className="font-medium text-foreground">
                Dividend dates update automatically every weekday at 18:30
                from CSE announcements.
              </span>{" "}
              Only officially declared dividends show XD and payment dates.
              Estimated rows (sourced from TradingView annual DPS) will
              switch to declared automatically once the company files the
              corporate action with CSE — usually 4–6 weeks before the XD
              date.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* ---------- Admin: dividend sync trigger ---------- */}
      {isAdmin && (
        <Card className="border-dashed border-primary/30 bg-primary/5" data-testid="dividends-admin-sync-card">
          <CardContent className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="space-y-1">
              <div className="text-sm font-medium flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" />
                Admin · Dividend Announcements Sync
              </div>
              <p className="text-xs text-muted-foreground max-w-2xl leading-snug">
                Pulls declared dividends from the CSE corporate-actions API and replaces matching{" "}
                <em>Estimated</em> rows with <em>Declared</em> rows (with confirmed XD + payment dates).
                Free, idempotent, and auto-runs every weekday at 18:30 SLT.
              </p>
              {lastSync && (
                <div className="text-[11px] text-muted-foreground tabular-nums" data-testid="dividends-sync-status">
                  Last run: {lastSync.scrape_date ? new Date(lastSync.scrape_date).toLocaleString() : "—"}
                  {lastSync.matched_dividends != null && (
                    <> · {lastSync.matched_dividends} declared · {lastSync.inserted ?? 0} inserted · {lastSync.updated ?? 0} updated</>
                  )}
                </div>
              )}
            </div>
            <Button
              onClick={handleSyncNow}
              disabled={syncing}
              size="sm"
              data-testid="dividends-sync-now-btn"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync Now"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ---------- KPI ribbon ---------- */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total dividend records" value={allDividends.length} testId="kpi-total-dividends" />
        <Stat label="Companies with dividends" value={distinctDividendCompanies} testId="kpi-distinct-companies" />
        <Stat label="Records — selected company" value={companyDividends.length} testId="kpi-selected-records" />
        <Stat label="Lifetime DPS — selected" value={formatLKR(totalDPS)} testId="kpi-selected-total" />
      </div>

      <Card className="border bg-card">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className="font-display">Dividend explorer</CardTitle>
              <p className="text-xs text-muted-foreground">
                Search by ticker or company name. Sorted alphabetically by ticker.
              </p>
            </div>
            <div className="md:w-[360px]">
              <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={pickerOpen}
                    className="w-full justify-between h-10"
                    data-testid="dividends-company-picker"
                  >
                    {selectedCompany ? (
                      <span className="flex items-center gap-2 min-w-0 truncate">
                        <span className="font-mono text-[11px] text-muted-foreground shrink-0">
                          {selectedCompany.company_code}
                        </span>
                        <span className="truncate text-sm">— {selectedCompany.company_name}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground inline-flex items-center gap-2">
                        <Search className="h-3.5 w-3.5" /> Select company
                      </span>
                    )}
                    <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[360px]" align="end">
                  <Command
                    filter={(value, search) => {
                      // Custom matcher: case-insensitive substring across code + name
                      // so typing "HNB" returns "HNB Hatton National Bank PLC".
                      if (!search) return 1;
                      return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                    }}
                  >
                    <CommandInput placeholder="Search ticker or company…" data-testid="dividends-company-search" />
                    <CommandList>
                      <CommandEmpty>No matches. Try a ticker code (e.g. HNB) or company name.</CommandEmpty>
                      <CommandGroup>
                        {companies.map((c) => {
                          // Include voting + non-voting tickers in the search target
                          const tokens = [
                            c.company_code,
                            c.company_name,
                            `${c.company_code}.N0000`,
                            `${c.company_code}.X0000`,
                            c.short_symbol || "",
                            c.cse_symbol || "",
                          ].filter(Boolean).join(" ");
                          const isSelected = c.company_code === selected;
                          return (
                            <CommandItem
                              key={c.company_code}
                              value={tokens}
                              onSelect={() => {
                                setSelected(c.company_code);
                                setPickerOpen(false);
                              }}
                              data-testid={`dividends-company-option-${c.company_code}`}
                            >
                              <Check className={`h-3.5 w-3.5 mr-2 ${isSelected ? "opacity-100 text-primary" : "opacity-0"}`} />
                              <span className="font-mono text-[11px] text-muted-foreground w-16 shrink-0">
                                {c.company_code}
                              </span>
                              <span className="truncate text-sm">— {c.company_name}</span>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Stat label="Records for this company" value={companyDividends.length} />
          <Stat label="Total DPS (lifetime)" value={formatLKR(totalDPS)} />
          <Stat label="Last ex-dividend" value={formatDate(lastEx)} />
        </CardContent>
      </Card>

      <TradingViewYieldCard fund={companyFund} companyCode={selected} hasHistory={companyDividends.length > 0} />

      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-6">
        <Card className="border bg-card">
          <CardHeader>
            <CardTitle className="font-display">Annual DPS</CardTitle>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: 288 }}>
              {yearChart.length === 0 ? (
                <div
                  className="h-full flex flex-col items-center justify-center text-center p-6 rounded-lg border border-dashed"
                  data-testid="dividends-empty-chart"
                >
                  <div className="text-sm font-medium mb-1">No annual DPS data yet</div>
                  <div className="text-xs text-muted-foreground max-w-sm leading-relaxed">
                    We&rsquo;ll plot annual dividend-per-share totals here as
                    the company announces dividends or publishes annual
                    reports. Check the timeline on the right for any historical
                    records.
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={288} key={`bar-${yearChart.length}`}>
                  <BarChart data={yearChart}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="year" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <RTooltip
                      contentStyle={{
                        background: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                      formatter={(v, _n, item) => [
                        `${formatLKR(v)}${item?.payload?.estimated ? " (est.)" : ""}`,
                        "DPS total",
                      ]}
                    />
                    <Bar dataKey="total" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                      {yearChart.map((row, idx) => (
                        <Cell
                          key={`cell-${idx}`}
                          fill={row.estimated ? "hsl(var(--muted-foreground))" : "hsl(var(--chart-1))"}
                          fillOpacity={row.estimated ? 0.55 : 1}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            {yearChartNote && (
              <div
                className="mt-2 text-[11px] text-muted-foreground italic"
                data-testid="dividends-chart-note"
              >
                {yearChartNote}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border bg-card">
          <CardHeader>
            <CardTitle className="font-display">Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <DividendTimeline items={companyDividends} />
          </CardContent>
        </Card>
      </div>

      <Card className="border bg-card">
        <CardHeader>
          <CardTitle className="font-display">Recent dividends across CSE</CardTitle>
          <p className="text-xs text-muted-foreground">
            Showing the {Math.min(allDividends.length, 80)} most recent records sorted by ex-dividend date.
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-card sticky top-0">
                <tr className="border-b">
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Company</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Sector</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Type</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Ex-date</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-left">Pay date</th>
                  <th className="px-4 py-3 text-xs uppercase tracking-wide text-muted-foreground text-right">DPS</th>
                </tr>
              </thead>
              <tbody>
                {allDividends.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      No dividend records yet. Data will appear when a company announces a dividend on CSE.
                    </td>
                  </tr>
                )}
                {allDividends.map((d, i) => {
                  const isEstimated = d.is_estimated || (d.dividend_type || "").toLowerCase() === "estimated";
                  const isDeclared = !isEstimated && (
                    d.status === "declared"
                    || d.source === "cse_announcement_api"
                    || d.source === "cse_official_daily"
                    || d.ex_dividend_date  // any row with a real XD date is declared
                  );
                  const subtype = (d.dividend_subtype || "cash").toLowerCase();
                  const isScrip = subtype === "scrip";
                  const isMixed = subtype === "mixed";
                  const typeLabel = (d.dividend_type || "declared").replace(/^./, (c) => c.toUpperCase());
                  const badgeClass = isEstimated
                    ? "bg-muted text-muted-foreground border-border"
                    : typeLabel === "Interim"
                      ? "bg-[hsl(var(--chart-2))]/15 text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/30"
                      : "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30";
                  // Per-row source label for the tooltip
                  const sourceLabel = (() => {
                    if (isEstimated) return "TradingView annual DPS estimate";
                    if (d.source === "cse_announcement_api") return "CSE corporate-actions API (declared)";
                    if (d.source === "cse_official_daily")   return "CSE Daily PDF (declared)";
                    if (d.source === "broker_capital_trust") return "Capital Trust Daily (declared)";
                    if (d.source === "annual_report")        return "Annual report (declared)";
                    return "Declared";
                  })();
                  return (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/40" data-testid={`div-row-${i}`}>
                      <td className="px-4 py-3">
                        {d.company_code ? (
                          <CompanyLink
                            companyCode={d.company_code}
                            name={d.company_name || d.cse_symbol || "—"}
                            fromLabel="Dividends"
                            testId={`div-name-${d.company_code}-${i}`}
                          />
                        ) : (
                          <span className="text-sm" data-testid={`div-name-${i}`}>
                            {d.company_name || d.cse_symbol || "—"}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{d.sector || "—"}</td>
                      <td className="px-4 py-3">
                        <Tooltip delayDuration={200}>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1.5 flex-wrap">
                              {isEstimated ? (
                                <Badge
                                  variant="outline"
                                  className="border-border text-muted-foreground inline-flex items-center gap-1"
                                  data-testid={`div-status-est-${i}`}
                                >
                                  <span aria-hidden>≈</span> Estimated
                                </Badge>
                              ) : (
                                <>
                                  <Badge
                                    variant="outline"
                                    className={`capitalize border ${badgeClass}`}
                                    data-testid={`div-badge-${i}`}
                                  >
                                    {typeLabel}
                                  </Badge>
                                  {isDeclared && (
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] px-1.5 py-0 border-[hsl(var(--success))]/40 text-[hsl(var(--success))]"
                                      data-testid={`div-status-decl-${i}`}
                                    >
                                      Declared
                                    </Badge>
                                  )}
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] px-1.5 py-0 border ${
                                      isScrip
                                        ? "bg-muted text-muted-foreground border-border"
                                        : isMixed
                                          ? "bg-[hsl(var(--chart-3))]/15 text-[hsl(var(--chart-3))] border-[hsl(var(--chart-3))]/30"
                                          : "bg-background text-muted-foreground border-border"
                                    }`}
                                    data-testid={`div-subtype-${i}`}
                                  >
                                    {isScrip ? "Scrip" : isMixed ? "Mixed" : "Cash"}
                                  </Badge>
                                </>
                              )}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs leading-relaxed">
                            {isEstimated
                              ? "Annual DPS from TradingView. Upload annual reports or wait for CSE announcement for exact dates."
                              : isScrip
                                ? `${sourceLabel}. Scrip dividend = bonus shares only. No cash to holders; not used for yield.`
                                : isMixed
                                  ? `${sourceLabel}. Combined cash + scrip dividend. Yield uses cash component only.`
                                  : `${sourceLabel}. ${typeLabel === "Interim" ? "Interim dividend declared by the company between annual financial reports." : "A dividend formally declared by the company and recorded at ex-dividend date."}`}
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      <td className="px-4 py-3" data-testid={`div-xd-${i}`}>
                        {isEstimated ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help text-muted-foreground border-b border-dashed border-muted-foreground/40">
                                —
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              XD date not yet announced. Will update when company
                              officially declares the dividend.
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          formatDate(d.xd_date || d.ex_dividend_date) || "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground" data-testid={`div-pay-${i}`}>
                        {isEstimated ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help text-muted-foreground border-b border-dashed border-muted-foreground/40">
                                —
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs text-xs">
                              Payment date not yet announced. Will update when company
                              officially declares the dividend.
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          d.payment_date ? formatDate(d.payment_date) : "—"
                        )}
                      </td>
                      <td className="px-4 py-3 text-right tabular" data-testid={`div-dps-${i}`}>
                        {isScrip
                          ? <span className="text-muted-foreground">— (bonus)</span>
                          : (d.dividend_per_share != null ? formatLKR(d.dividend_per_share) : "—")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
    </TooltipProvider>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border bg-background p-3" data-testid={testId}>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="mt-1 text-base font-semibold tabular">{value ?? "—"}</div>
  </div>
);

const TradingViewYieldCard = ({ fund, companyCode, hasHistory }) => {
  if (!fund) return null;
  const yld = fund.dividend_yield;
  const ttm = fund.dividend_yield_ttm;
  const realDps = fund.dividend_per_share;
  const estDps = fund.estimated_dps;
  const dpsSource = fund.dps_source; // tv_actual | tv_estimated
  const payout = fund.dividend_payout_ratio;
  if (yld == null && realDps == null && estDps == null) return null;
  const isEstimate = !realDps && estDps != null;
  return (
    <Card className="border bg-card" data-testid="tv-yield-snapshot">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="font-display text-base">TradingView dividend snapshot</CardTitle>
          <Badge variant="outline" className="text-[10px] gap-1">
            Source: TradingView · live
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tv-yield-grid">
          <div className="rounded-lg border bg-background p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Current yield</div>
            <div className="mt-1 text-xl font-display font-semibold tabular text-[hsl(var(--success))]">
              {yld != null ? `${yld.toFixed(2)}%` : "—"}
            </div>
            {ttm != null && Math.abs((ttm || 0) - (yld || 0)) > 0.01 && (
              <div className="text-[10px] text-muted-foreground mt-0.5">TTM: {ttm.toFixed(2)}%</div>
            )}
          </div>
          <div className="rounded-lg border bg-background p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {isEstimate ? "Est. annual DPS" : "Annual DPS"}
            </div>
            <div className="mt-1 text-xl font-display font-semibold tabular">
              {realDps ? formatLKR(realDps) : (estDps ? `${formatLKR(estDps)}` : "—")}
              {isEstimate && (
                <span className="ml-1.5 text-[10px] text-muted-foreground font-sans normal-case">est.</span>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {dpsSource === "tv_actual" && "From TV `dps_common_stock_prim_issue_fy`"}
              {dpsSource === "tv_estimated" && "Computed yield × close ÷ 100"}
            </div>
          </div>
          <div className="rounded-lg border bg-background p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Payout ratio (TTM)</div>
            <div className="mt-1 text-xl font-display font-semibold tabular flex items-center gap-1.5">
              {payout != null && payout > 0
                ? `${payout.toFixed(1)}%`
                : (yld != null && yld > 0)
                  ? "—"
                  : "—"}
              {payout != null && payout > 100 && (
                <Badge
                  variant="outline"
                  className="text-[9px] bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 font-normal"
                  data-testid="tv-payout-high"
                >
                  &gt;100%
                </Badge>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
              {payout != null && payout > 100
                ? "Paying more than earnings — may not be sustainable."
                : (payout == null || payout === 0) && yld != null && yld > 0
                  ? "Payout ratio data unavailable from TradingView."
                  : "Share of earnings paid as dividends"}
            </div>
          </div>
          <div className="rounded-lg border bg-background p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Declared history</div>
            <div className="mt-1 text-xl font-display font-semibold tabular">
              {hasHistory ? "On file" : "—"}
            </div>
            <div className="text-[10px] text-muted-foreground mt-0.5">
              {hasHistory ? "See timeline below" : "No declared dividends recorded yet"}
            </div>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
          Yield data is sourced from TradingView and refreshed weekly (Sun 08:00 Asia/Colombo) plus
          a monthly DPS top-up on the 1st. For exact dividend history and declared amounts,
          upload the {companyCode} annual report on the Stock Detail → Reports tab and Claude will
          extract every declared payment.
        </p>
      </CardContent>
    </Card>
  );
};

export default DividendsPage;
